'use client';

import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PainelPermissionsDialogShell } from '@/components/painel/permissions/painel-role-visuals';
import {
  painelButtonPrimaryClass,
  painelModalCancelButtonClass,
} from '@/components/painel/painel-styles';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import { PowerSectionSmileyIcon } from '@/components/help-center/powers/power-gallery-view';
import { cn } from '@/lib/utils';

type LoreHeroSmileyModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string;
  onConfirm: (next: string) => void;
  /** Sugestões rápidas (ex.: smilies do power). */
  suggestions?: string[];
  powerName?: string;
};

function cleanCode(raw: string): string {
  return raw.replace(/[()]/g, '').trim().toLowerCase();
}

export function LoreHeroSmileyModal({
  open,
  onOpenChange,
  value,
  onConfirm,
  suggestions = [],
  powerName,
}: LoreHeroSmileyModalProps) {
  const [draft, setDraft] = useState(() => cleanCode(value) || 'smile');

  useEffect(() => {
    if (!open) return;
    setDraft(cleanCode(value) || 'smile');
  }, [open, value]);

  const preview = cleanCode(draft) || 'smile';
  const uniqueSuggestions = [
    ...new Set(
      suggestions
        .map(cleanCode)
        .filter((s) => s.length > 0)
    ),
  ].slice(0, 12);

  const canConfirm = preview.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[440px] gap-0 overflow-hidden border-0 bg-transparent p-0 shadow-none">
        <PainelPermissionsDialogShell>
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#030023]">
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#000b6a]/50 via-transparent to-emerald-500/10"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-sky-400/15 blur-3xl"
              aria-hidden
            />

            <div className="relative border-b border-white/10 px-6 pb-5 pt-6">
              <div className="flex items-start gap-3">
                <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-sky-500/15 ring-1 ring-sky-400/30">
                  <Sparkles className="h-5 w-5 text-sky-300" aria-hidden />
                </span>
                <div className="min-w-0 pt-0.5">
                  <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300/55">
                    Lore
                  </p>
                  <DialogTitle className="mt-1 font-poppins text-lg font-bold text-white">
                    Smiley em destaque
                  </DialogTitle>
                  <p className="mt-1 font-inter text-sm leading-snug text-slate-400">
                    {powerName
                      ? `Escolha o smiley principal da história de ${powerName}.`
                      : 'Escolha o smiley principal desta história.'}
                  </p>
                </div>
              </div>
            </div>

            <div className="relative space-y-5 px-6 py-5">
              <div className="flex flex-col items-center gap-3">
                <div className="relative flex h-28 w-28 items-center justify-center">
                  <div
                    className="absolute inset-0 scale-125 rounded-full bg-emerald-400/15 blur-2xl"
                    aria-hidden
                  />
                  <div className="relative flex h-[5.5rem] w-[5.5rem] items-center justify-center rounded-full border border-emerald-400/35 bg-[#020018]/80 p-2.5 shadow-[0_0_24px_rgba(52,211,153,0.18)]">
                    <PowerSectionSmileyIcon code={preview} size="hero" />
                  </div>
                </div>
                <p className="font-mono text-xs text-slate-500">({preview})</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lore-hero-smiley" className="font-poppins text-slate-400">
                  Código do smiley
                </Label>
                <Input
                  id="lore-hero-smiley"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value.replace(/[()]/g, ''))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canConfirm) {
                      e.preventDefault();
                      onConfirm(preview);
                      onOpenChange(false);
                    }
                  }}
                  placeholder="ex.: smile, topman, purple, gold"
                  autoFocus
                  className={cn(painelInputClass, 'font-mono')}
                />
                <p className="font-inter text-[11px] text-slate-500">
                  Sem parênteses. Qualquer smiley ou ícone de power do xat.
                </p>
              </div>

              {uniqueSuggestions.length > 0 ? (
                <div className="space-y-2">
                  <p className="font-poppins text-[11px] font-semibold uppercase tracking-[0.16em] text-sky-300/70">
                    Deste power
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {uniqueSuggestions.map((code) => {
                      const active = code === preview;
                      return (
                        <button
                          key={code}
                          type="button"
                          onClick={() => setDraft(code)}
                          className={cn(
                            'inline-flex items-center gap-2 rounded-xl border px-2.5 py-1.5 transition',
                            active
                              ? 'border-sky-400/45 bg-sky-500/15 ring-1 ring-sky-400/35'
                              : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.06]'
                          )}
                        >
                          <PowerSectionSmileyIcon code={code} size="md" />
                          <span className="font-mono text-[11px] text-slate-300">{code}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="relative flex flex-col-reverse gap-2 border-t border-white/10 bg-blue-950/25 px-6 py-4 sm:flex-row sm:justify-end sm:gap-3">
              <Button
                type="button"
                variant="ghost"
                className={painelModalCancelButtonClass}
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={!canConfirm}
                className={cn(painelButtonPrimaryClass, 'h-10 px-5')}
                onClick={() => {
                  if (!canConfirm) return;
                  onConfirm(preview);
                  onOpenChange(false);
                }}
              >
                Guardar smiley
              </Button>
            </div>
          </div>
        </PainelPermissionsDialogShell>
      </DialogContent>
    </Dialog>
  );
}
